import type { WeaponDefinition, WeaponContext, WeaponResult } from './types';
import { Projectile } from '../entities/projectile';
import { computeSafeSpawnPosition } from './spawnHelper';

/**
 * Lightning (GunBound mobile - Thor's Hammer). An electric ball is lobbed in
 * an arc; the moment it lands (terrain or a tank) it explodes AND calls a
 * vertical lightning bolt straight down from directly above the impact (the
 * bolt is blocked by any terrain overhead and craters where it lands - see
 * Game/HeadlessGame.queueLightning, which reuses the Thor-zap machinery).
 *
 * - T1 (`lightningT1`): a single vertical bolt on the impact point.
 * - T2 (`lightningT2`): a "V" of two bolts splaying onto points either side.
 */
function makeLightning(id: 'lightningT1' | 'lightningT2', name: string): WeaponDefinition {
  return {
    id,
    name,
    icon: `${id}.png`,
    category: 'thrown',
    maxAmmo: id === 'lightningT1' ? 4 : 2,
    maxUsesPerTurn: 1,
    powerCharging: true,
    timerAdjustable: false,
    showAimGuide: true,
    allowRetreat: true,

    fire(ctx: WeaponContext): WeaponResult {
      const { worm, angle, power, world } = ctx;

      const speed = (power / 100) * 750;
      const rad = (angle * Math.PI) / 180;
      const aimRad = worm.facing === 'right' ? rad : Math.PI - rad;
      const spawn = computeSafeSpawnPosition(worm, aimRad, 20, world);

      // No contact explosion; the lightning bolt(s) are the entire payload,
      // called via onProjectileExploded by the type.
      const ball = new Projectile({
        x: spawn.x,
        y: spawn.y,
        vx: Math.cos(aimRad) * speed,
        vy: -Math.sin(aimRad) * speed,
        type: id,
        timer: -1,
        windAffected: true,
        ownerId: worm.id,
        noContactExplosion: true,
      });

      return { success: true, entities: [ball] };
    },
  };
}

export const WeaponLightningT1 = makeLightning('lightningT1', 'Lightning');
export const WeaponLightningT2 = makeLightning('lightningT2', 'Lightning V');
